<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import PipelinesAPI from 'dashboard/api/crm/pipelines';
import StagesAPI from 'dashboard/api/crm/stages';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const enabled = ref(false);
const pipelineId = ref(null);
const stageId = ref(null);
const pipelines = ref([]);
const stages = ref([]);
const loadingPipelines = ref(false);
const loadingStages = ref(false);
const saving = ref(false);

async function fetchPipelines() {
  loadingPipelines.value = true;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = (data?.payload || []).filter(p => !p.archived);
  } catch (e) {
    useAlert(t('INBOX_MGMT.CRM.LOAD_PIPELINES_ERROR'));
  } finally {
    loadingPipelines.value = false;
  }
}

// Adversarial fix: when user rapidly toggles between pipelines, fetchStages
// requests can resolve out-of-order. Track the current request id and discard
// stale responses so the dropdown never shows stages from a wrong pipeline.
let stageFetchSeq = 0;

async function fetchStages(pid) {
  if (!pid) {
    stages.value = [];
    return;
  }
  const mySeq = ++stageFetchSeq;
  loadingStages.value = true;
  try {
    const { data } = await StagesAPI.list(pid);
    if (mySeq !== stageFetchSeq) return; // a newer request started — drop result
    stages.value = (data?.payload || []).sort((a, b) => a.position - b.position);
  } catch (e) {
    if (mySeq !== stageFetchSeq) return;
    useAlert(t('INBOX_MGMT.CRM.LOAD_STAGES_ERROR'));
  } finally {
    if (mySeq === stageFetchSeq) loadingStages.value = false;
  }
}

watch(pipelineId, async newId => {
  await fetchStages(newId);
  // If the previously selected stage doesn't belong to the new pipeline, reset.
  if (newId && stageId.value && !stages.value.some(s => s.id === stageId.value)) {
    stageId.value = null;
  }
});

function loadFromInbox() {
  const cfg = props.inbox.crm_auto_create_config || {};
  enabled.value = !!cfg.enabled;
  pipelineId.value = cfg.pipeline_id || null;
  stageId.value = cfg.stage_id || null;
}

const canSave = computed(() => {
  if (!enabled.value) return true;
  return !!pipelineId.value && !!stageId.value;
});

async function save() {
  saving.value = true;
  try {
    const payload = {
      id: props.inbox.id,
      formData: false,
      crm_auto_create_config: {
        enabled: enabled.value,
        pipeline_id: enabled.value ? pipelineId.value : null,
        stage_id: enabled.value ? stageId.value : null,
      },
    };
    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (e) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  loadFromInbox();
  await fetchPipelines();
  if (pipelineId.value) await fetchStages(pipelineId.value);
});
</script>

<template>
  <div class="p-4 space-y-5 max-w-2xl">
    <header>
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('INBOX_MGMT.CRM.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('INBOX_MGMT.CRM.DESCRIPTION') }}
      </p>
    </header>

    <div class="space-y-3">
      <label class="flex items-start gap-2 cursor-pointer">
        <input
          v-model="enabled"
          type="checkbox"
          class="mt-1"
        />
        <span>
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.CRM.ENABLE_LABEL') }}
          </span>
          <span class="block text-xs text-n-slate-11 mt-0.5">
            {{ t('INBOX_MGMT.CRM.ENABLE_HINT') }}
          </span>
        </span>
      </label>
    </div>

    <div v-if="enabled" class="space-y-3 border-l-2 border-n-brand pl-4">
      <div class="space-y-1">
        <label class="text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.CRM.PIPELINE_LABEL') }}
        </label>
        <select
          v-model="pipelineId"
          class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-sm text-n-slate-12"
          :disabled="loadingPipelines"
        >
          <option :value="null">
            {{ t('INBOX_MGMT.CRM.PIPELINE_PLACEHOLDER') }}
          </option>
          <option v-for="p in pipelines" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
      </div>

      <div class="space-y-1">
        <label class="text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.CRM.STAGE_LABEL') }}
        </label>
        <select
          v-model="stageId"
          class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-sm text-n-slate-12 disabled:opacity-50"
          :disabled="!pipelineId || loadingStages"
        >
          <option :value="null">
            {{ t('INBOX_MGMT.CRM.STAGE_PLACEHOLDER') }}
          </option>
          <option v-for="s in stages" :key="s.id" :value="s.id">
            {{ s.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="pt-2">
      <button
        class="px-4 py-2 rounded bg-n-brand text-white text-sm disabled:opacity-50"
        :disabled="!canSave || saving"
        @click="save"
      >
        {{ saving ? t('INBOX_MGMT.CRM.SAVING') : t('INBOX_MGMT.CRM.SAVE') }}
      </button>
    </div>
  </div>
</template>
